import React from 'react'

import { Link, useNavigate } from 'react-router-dom';
import Pagination from './Pagination.jsx';
import PriceRequestsManager from './PriceRequestsManager.jsx';
import QuoteRequestsManager from './QuoteRequestsManager.jsx';
import NotificationManager from './NotificationManager.jsx';
import RejectProductModal from './RejectProductModal.jsx';
import ProductModal from './ProductModal.jsx';
import AddItemWizard from './AddItemWizard.jsx';
import EditListingModal from './EditListingModal.jsx';
// c:\xampp\htdocs\miners-market-main\components\AdminDashboard.js
// Helper function to format price, in case it's not globally available.

// --- NEW: Modal to prompt for profile completion ---
// This is the same modal used in SellerDashboard.js
function ProfileIncompleteModal({ isOpen, onClose, missingFields }) {
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleGoToProfile = () => {
        onClose();
        navigate('/profile');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Complete Your Profile</h2>
                <p className="text-gray-600 mb-4">To add a new listing, you must first complete your admin profile. This ensures smooth and secure transactions for everyone.</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-red-800 mb-2">Missing Information:</h3>
                    <ul className="list-disc list-inside text-red-700 space-y-1">
                        {missingFields.map(field => <li key={field}>{field}</li>)}
                    </ul>
                </div>
                <div className="flex justify-end space-x-4">
                    <button onClick={onClose} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleGoToProfile} className="primary-gradient text-white py-2 px-4 rounded-lg hover:opacity-90">Go to My Profile</button>
                </div>
            </div>
        </div>
    );
}

function formatPrice(price) {
    const num = parseFloat(price);
    return isNaN(num) ? 'R 0.00' : `R ${num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// --- NEW: UserActionsDropdown Sub-Component ---
// By extracting this logic, we make the main component's render method cleaner
// and create a reusable, self-contained piece of UI.
function UserActionsDropdown({ user, onAction }) {
    const [isOpen, setIsOpen] = React.useState(false);
    const dropdownRef = React.useRef(null);

    // Effect to handle clicks outside of the dropdown to close it.
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleActionClick = (action) => {
        onAction(user, action);
        setIsOpen(false); // Close dropdown after action
    };

    return (
        <div className="relative inline-block text-left" ref={dropdownRef}>
            <button onClick={() => setIsOpen(!isOpen)} className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none">
                Actions <i className="fas fa-chevron-down -mr-1 ml-2 h-5 w-5"></i>
            </button>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {user.status === 'pending' && <a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('verify'); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Verify</a>}
                        {user.status === 'verified' && <><a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('suspend'); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Suspend</a><a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('ban'); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Ban</a></>}
                        {user.status === 'suspended' && <><a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('un-suspend'); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Re-activate</a><a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('ban'); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Ban</a></>}
                        {user.status === 'pending_review' && <><a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('verify'); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Approve & Verify</a><a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('suspend'); }} className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" role="menuitem">Reject (Suspend)</a></>}
                        <div className="border-t border-gray-100 my-1"></div>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('manage_roles'); }} className="block px-4 py-2 text-sm text-blue-700 hover:bg-blue-50" role="menuitem">Manage Roles</a>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleActionClick('delete'); }} className="block px-4 py-2 text-sm text-red-700 hover:bg-red-50" role="menuitem">Delete User</a>
                    </div>
                </div>
            )}
        </div>
    );
}

function AdminDashboard({ user, onViewProductById }) {
        const navigate = useNavigate();
        const [totalRevenue, setTotalRevenue] = React.useState(0);
        const [totalTransactions, setTotalTransactions] = React.useState(0);
        const [pendingItemsCount, setPendingItemsCount] = React.useState(0);
        const [activeUsersCount, setActiveUsersCount] = React.useState(0);
        const [dieselQuotesCount, setDieselQuotesCount] = React.useState(0);
        const [pendingProducts, setPendingProducts] = React.useState([]);
        const [loadingStats, setLoadingStats] = React.useState(true);
        const [loadingPendingProducts, setLoadingPendingProducts] = React.useState(true);
        const [loadingRecentProducts, setLoadingRecentProducts] = React.useState(true);
        const [error, setError] = React.useState(null);
        const [productToView, setProductToView] = React.useState(null);
        const [actionMessage, setActionMessage] = React.useState(null);
        const [userActionMessage, setUserActionMessage] = React.useState(null);
        const [showRejectModal, setShowRejectModal] = React.useState(false);
        const [productToReject, setProductToReject] = React.useState(null);

        // --- User Management State ---
        const [allUsers, setAllUsers] = React.useState([]);
        const [loadingAllUsers, setLoadingAllUsers] = React.useState(true);
        const [showUserActionModal, setShowUserActionModal] = React.useState(false);
        const [userSearchTerm, setUserSearchTerm] = React.useState('');
        const [userRoleFilter, setUserRoleFilter] = React.useState('all'); // NEW: State for role filter
        const [userActionModalConfig, setUserActionModalConfig] = React.useState({ user: null, action: '' });
    const [recentProducts, setRecentProducts] = React.useState([]);
        // --- NEW: State for the new suspension modal ---
        const [showSuspendModal, setShowSuspendModal] = React.useState(false);
        const [userToSuspend, setUserToSuspend] = React.useState(null);
        const [suspensionReason, setSuspensionReason] = React.useState('');
        const [suspensionError, setSuspensionError] = React.useState(null);
        // --- NEW: State for the document review modal ---
        const [showReviewModal, setShowReviewModal] = React.useState(false);
        const [userToReview, setUserToReview] = React.useState(null);
        const [reviewDocuments, setReviewDocuments] = React.useState([]);
        const [loadingReviewDocs, setLoadingReviewDocs] = React.useState(false);
        const [reviewDocsError, setReviewDocsError] = React.useState(null);
        // --- NEW: State for the rejection reason modal ---
        const [showRejectReviewModal, setShowRejectReviewModal] = React.useState(false);
        const [rejectionReason, setRejectionReason] = React.useState('');
        const [rejectionError, setRejectionError] = React.useState(null);
        // --- NEW: State for the role management modal ---
        const [showRoleModal, setShowRoleModal] = React.useState(false);
        const [userToManageRoles, setUserToManageRoles] = React.useState(null);
        const [selectedRoles, setSelectedRoles] = React.useState([]);
        const [roleError, setRoleError] = React.useState(null);

        // --- State for WOTS Products List ---
        const [wotsProducts, setWotsProducts] = React.useState([]);
        const [loadingWotsProducts, setLoadingWotsProducts] = React.useState(true);

        // --- State for Pending Orders (Awaiting Payment) ---
        const [pendingOrders, setPendingOrders] = React.useState([]);
        const [loadingPendingOrders, setLoadingPendingOrders] = React.useState(true);

        // --- State for Paid Orders (Eligible for Reversal) ---
        const [paidOrders, setPaidOrders] = React.useState([]);
        const [loadingPaidOrders, setLoadingPaidOrders] = React.useState(true);
        const [paidOrdersPage, setPaidOrdersPage] = React.useState(1);

        // --- State for Cancel Transaction Modal ---
        const [showCancelConfirmModal, setShowCancelConfirmModal] = React.useState(false);
        const [orderToCancel, setOrderToCancel] = React.useState(null);
        const [isCancelling, setIsCancelling] = React.useState(false);
        const [cancelError, setCancelError] = React.useState(null);
        
        // --- State for Approval Options Modal ---
        const [showApproveOptionsModal, setShowApproveOptionsModal] = React.useState(false);
        const [productToApprove, setProductToApprove] = React.useState(null);

        // --- NEW: State for universal Edit and Delete modals ---
        const [showEditListingModal, setShowEditListingModal] = React.useState(false);
        const [listingToEdit, setListingToEdit] = React.useState(null);
        const [showDeleteConfirmModal, setShowDeleteConfirmModal] = React.useState(false);
        const [listingToDelete, setListingToDelete] = React.useState(null);

        // --- Pagination State ---
        const [pendingPage, setPendingPage] = React.useState(1);
        const [recentPage, setRecentPage] = React.useState(1);
        const [wotsPage, setWotsPage] = React.useState(1);
        const [allUsersPage, setAllUsersPage] = React.useState(1);
        const [pendingOrdersPage, setPendingOrdersPage] = React.useState(1);
        const itemsPerPage = 5;
        const [recentSearchTerm, setRecentSearchTerm] = React.useState('');

        // --- State for Add Item Wizard ---
        const [showAddItemWizard, setShowAddItemWizard] = React.useState(false);
        const [categories, setCategories] = React.useState([]);
        const [isSubmitting, setIsSubmitting] = React.useState(false);
        // --- NEW: State for profile check ---
        const [showProfileIncompleteModal, setShowProfileIncompleteModal] = React.useState(false);
        const [missingProfileFields, setMissingProfileFields] = React.useState([]);
        const [isCheckingProfile, setIsCheckingProfile] = React.useState(false);

        const [formError, setFormError] = React.useState(null);

        // Fetch Dashboard Stats
        const fetchDashboardStats = React.useCallback(async () => {
            try {
                setLoadingStats(true);
                const response = await fetch('/api/admin_dashboard_stats.php', { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setTotalRevenue(data.totalRevenue);
                setTotalTransactions(data.totalTransactions);
                setPendingItemsCount(data.pendingItemsCount);
                setActiveUsersCount(data.activeUsersCount);
                
                // Fetch diesel quotes count
                try {
                    const dieselResponse = await fetch('/api/get_diesel_quotes_count.php', { credentials: 'include' });
                    if (dieselResponse.ok) {
                        const dieselData = await dieselResponse.json();
                        setDieselQuotesCount(dieselData.count || 0);
                    }
                } catch (err) {
                    console.warn('Could not fetch diesel quotes count:', err);
                    setDieselQuotesCount(0);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingStats(false);
            }
        }, []);

        // Fetch Pending Products
        const fetchPendingProducts = React.useCallback(async () => {
            try {
                setLoadingPendingProducts(true);
                const response = await fetch('/api/admin_pending_products.php', { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setPendingProducts(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingPendingProducts(false);
            }
        }, []);

        // Fetch Recent Products (recently approved)
        const fetchRecentProducts = React.useCallback(async () => {
            try {
                setLoadingRecentProducts(true);
                const response = await fetch('/api/admin_recent_products.php', { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setRecentProducts(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoadingRecentProducts(false);
            }
        }, []);

        // Fetch All Users for Management
        const fetchAllUsers = React.useCallback(async () => {
            try {
                setLoadingAllUsers(true);
                const response = await fetch('/api/admin_get_all_users.php', { credentials: 'include' });
                if (!response.ok) throw new Error('Could not fetch users.');
                const data = await response.json();
                setAllUsers(data);
            } catch (err) {
                setError(prev => prev ? `${prev} | ${err.message}` : err.message);
            } finally {
                setLoadingAllUsers(false);
            }
        }, []);

        // Fetch WOTS Products
        const fetchWotsProducts = React.useCallback(async () => {
            try {
                setLoadingWotsProducts(true);
                const response = await fetch('/api/admin_wots_products.php', { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setWotsProducts(data);
            } catch (err) {
                setError(prev => prev ? `${prev} | ${err.message}` : err.message);
            } finally {
                setLoadingWotsProducts(false);
            }
        }, []);

        // Fetch Pending Orders (Awaiting Payment)
        const fetchPendingOrders = React.useCallback(async () => {
            try {
                setLoadingPendingOrders(true);
                const response = await fetch('/api/admin_get_pending_orders.php', { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setPendingOrders(data);
            } catch (err) {
                setError(prev => prev ? `${prev} | ${err.message}` : err.message);
            } finally {
                setLoadingPendingOrders(false);
            }
        }, []);

        // Fetch Paid Orders (Eligible for Reversal)
        const fetchPaidOrders = React.useCallback(async () => {
            try {
                setLoadingPaidOrders(true);
                const response = await fetch('/api/admin_get_paid_orders.php', { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setPaidOrders(data);
            } catch (err) {
                setError(prev => prev ? `${prev} | ${err.message}` : err.message);
            } finally {
                setLoadingPaidOrders(false);
            }
        }, []);

        // Fetch Categories for the form
        const fetchCategories = React.useCallback(async () => {
            try {
                const response = await fetch(`/api/categories.php?t=${new Date().getTime()}`, { credentials: 'include' });
                if (!response.ok) throw new Error('Could not fetch categories.');
                const data = await response.json();
                setCategories(data);
            } catch (err) {
                setError(prev => prev ? `${prev} | ${err.message}` : err.message);
            }
        }, []);

// --- NEW: Handle anchor scrolling for admin navigation ---
    React.useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            // Remove the # and find the element
            const elementId = hash.substring(1);
            const element = document.getElementById(elementId);
            
            if (element) {
                // Small delay to ensure content is rendered
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    }, []); // Run once when component mounts

// Initial data fetch on component mount
React.useEffect(() => {
    fetchDashboardStats();
    fetchPendingProducts();
    fetchRecentProducts();
    fetchAllUsers();
    fetchCategories();
    fetchWotsProducts();
    fetchPendingOrders();
    fetchPaidOrders();
}, [fetchDashboardStats, fetchPendingProducts, fetchRecentProducts, fetchAllUsers, fetchCategories, fetchWotsProducts, fetchPendingOrders, fetchPaidOrders]);
        // --- NEW: Auto-refresh data for "real-time" updates ---
        React.useEffect(() => {
            // Set up an interval to poll for new data every 30 seconds.
            const autoRefreshInterval = setInterval(() => {
                fetchDashboardStats();
                fetchPendingProducts();
                fetchPendingOrders();
                fetchPaidOrders();
            }, 30000);

            return () => clearInterval(autoRefreshInterval);
        }, [fetchDashboardStats, fetchPendingProducts, fetchPendingOrders, fetchPaidOrders]);

        // --- NEW: Handler to pass product view requests up to the main App component ---
        const handleViewProductById = (productId, listingType) => {
            onViewProductById(productId, listingType);
        };

        // --- NEW: Handler for when a listing is updated via the modal ---
        const handleListingUpdated = () => {
            fetchRecentProducts();
            fetchPendingProducts();
            fetchWotsProducts();
        };

        // --- NEW: Handlers for universal Edit and Delete actions ---
        const handleEditClick = (listing) => {
            setListingToEdit(listing);
            setShowEditListingModal(true);
        };

        const handleDeleteClick = (listing) => {
            setListingToDelete(listing);
            setShowDeleteConfirmModal(true);
        };

        const confirmDelete = async () => {
            if (!listingToDelete) return;
            setIsSubmitting(true);
            setActionMessage(null);
            try {
                const response = await fetch('/api/delete_listing.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: listingToDelete.id,
                        listingType: listingToDelete.listingType
                    })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || 'Failed to delete listing.');
                
                setActionMessage({ type: 'success', text: result.message });
                // Refresh all relevant lists
                fetchRecentProducts();
                fetchPendingProducts();
                fetchWotsProducts();
                fetchDashboardStats();
                setShowDeleteConfirmModal(false);
                setListingToDelete(null);
            } catch (err) {
                setActionMessage({ type: 'error', text: err.message });
            } finally {
                setIsSubmitting(false);
            }
        };

        // --- REFACTORED: Centralized function to update status for ANY listing type ---
        const updateListingStatus = async (product, status) => {
            setActionMessage(null);
            try {
                const response = await fetch('/api/update_listing_status.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        product_id: product.id, 
                        listing_type: product.listingType,
                        status: status, 
                        admin_id: user.id 
                    })
                });
                const result = await response.json();
                if (!response.ok) {
                    throw new Error(`For "${product.name}": ${result.message}` || `Failed to ${status} product.`);
                }
                setActionMessage({ type: 'success', text: result.message });
                fetchPendingProducts();
                fetchDashboardStats();
                fetchRecentProducts();
            } catch (err) {
                setActionMessage({ type: 'error', text: err.message });
            }
        };

        const handleListingAdded = () => {
            fetchWotsProducts();
        };

        // --- NEW: Handler for the "Add WOTS Item" button click ---
        const handleAddNewItemClick = async () => {
            setIsCheckingProfile(true);
            setActionMessage(null); // Clear previous messages
            try {
                const response = await fetch('/api/check_profile_status.php', {
                    credentials: 'include'
                });
                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result.error || 'Could not verify profile status.');
                }
    
                if (result.isComplete) {
                    setShowAddItemWizard(true);
                } else {
                    setMissingProfileFields(result.missingFields || []);
                    setShowProfileIncompleteModal(true);
                }
            } catch (err) {
                setActionMessage({ type: 'error', text: err.message });
            } finally {
                setIsCheckingProfile(false);
            }
        };

        // --- Handlers for Cancel Transaction ---
        const handleCancelClick = (order) => {
            setOrderToCancel(order);
            setShowCancelConfirmModal(true);
            setCancelError(null);
        };

        const confirmCancelTransaction = async () => {
            if (!orderToCancel) return;
            setIsCancelling(true);
            setCancelError(null);
            setActionMessage(null);

            try {
                const payload = {
                    order_id: orderToCancel.id,
                    tradesafe_transaction_id: orderToCancel.tradesafe_transaction_id,
                    product_id: orderToCancel.product_id,
                    quantity: orderToCancel.quantity,
                    listing_type: orderToCancel.listing_type,
                };

                const response = await fetch('/api/admin_cancel_transaction.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const result = await response.json();
                if (!response.ok) throw new Error(result.error || 'Failed to cancel transaction.');

                setActionMessage({ type: 'success', text: result.message });
                fetchPendingOrders();
                fetchPaidOrders();
                setShowCancelConfirmModal(false);
            } catch (err) {
                setCancelError(err.message);
            } finally {
                setIsCancelling(false);
            }
        };

        // --- Handler for viewing a WOTS product ---
        const handleViewWotsClick = (product) => {
            setProductToView({ ...product, listingType: 'wots' });
        };

        // --- Handlers for User Management Actions ---
        const handleUserAction = (user, action) => {
            const config = { user, action };
            switch (action) {
                case 'verify':
                    config.title = 'Confirm Verification';
                    config.message = `Are you sure you want to change <strong>${user.name}</strong>'s status to 'verified'?`;
                    config.buttonText = 'Verify';
                    config.buttonClass = 'bg-green-600 hover:bg-green-700';
                    break;
                // --- UPDATED: This now opens the new suspension modal ---
                case 'suspend':
                    // Instead of using the generic modal, we set state for our new modal
                    setUserToSuspend(user);
                    setSuspensionReason(''); // Clear previous reason
                    setSuspensionError(null);
                    setShowSuspendModal(true);
                    return; // Exit here to prevent generic modal from showing
                // --- END OF UPDATE ---
                case 'ban':
                    config.title = 'Confirm Ban';
                    config.message = `Are you sure you want to ban <strong>${user.name}</strong>? This is a permanent status.`;
                    config.buttonText = 'Ban';
                    config.buttonClass = 'bg-orange-600 hover:bg-orange-700';
                    break;
                case 'review_documents':
                    // This action opens the new review modal directly
                    handleReviewDocuments(user);
                    return; // Exit to prevent generic modal
                case 'delete':
                    config.title = 'Confirm Deletion';
                    config.message = `Are you sure you want to permanently delete <strong>${user.name}</strong>? This will remove all their products and cannot be undone.`;
                    config.buttonText = 'Delete Permanently';
                    config.buttonClass = 'bg-red-600 hover:bg-red-700';
                    break;
                case 'un-suspend':
                    config.title = 'Confirm Reactivation';
                    config.message = `Are you sure you want to reactivate <strong>${user.name}</strong>'s account? They will be able to log in again.`;
                    config.buttonText = 'Reactivate (Set to Verified)';
                    config.buttonClass = 'bg-green-600 hover:bg-green-700';
                    break;
                case 'manage_roles':
                    // Open the role management modal
                    setUserToManageRoles(user);
                    // Parse existing roles from comma-separated string
                    const existingRoles = user.roles ? user.roles.split(',').map(r => r.trim()) : [];
                    setSelectedRoles(existingRoles);
                    setRoleError(null);
                    setShowRoleModal(true);
                    return; // Exit to prevent generic modal
            }
            setUserActionModalConfig(config);
            setShowUserActionModal(true);
        };

        // --- NEW: Handler for submitting the suspension with a reason ---
        const handleConfirmSuspension = async () => {
            if (!userToSuspend || !suspensionReason.trim()) {
                setSuspensionError('A reason for suspension is required.');
                return;
            }
            setIsSubmitting(true);
            setSuspensionError(null);
            setUserActionMessage(null);

            try {
                // This will call a new API endpoint we will create in the next step
                const response = await fetch('/api/admin_suspend_user.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: userToSuspend.id, reason: suspensionReason })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || 'Failed to suspend user.');
                setUserActionMessage(result.message);
                fetchAllUsers(); // Refresh user list
                setShowSuspendModal(false);
            } catch (err) {
                setSuspensionError(err.message);
            } finally {
                setIsSubmitting(false);
            }
        };

        // --- NEW: Handlers for the document review process ---
        const handleReviewDocuments = async (user) => {
            setUserToReview(user);
            setShowReviewModal(true);
            setLoadingReviewDocs(true);
            setReviewDocsError(null);
            setReviewDocuments([]);

            try {
                const response = await fetch(`/api/admin_get_user_documents.php?user_id=${user.id}`, { credentials: 'include' });
                if (!response.ok) {
                    throw new Error(`Failed to fetch documents (Status: ${response.status})`);
                }
                const docs = await response.json();
                setReviewDocuments(docs);
            } catch (err) {
                setReviewDocsError(err.message);
            } finally {
                setLoadingReviewDocs(false);
            }
        };

        const handleApproveDocuments = async () => {
            if (!userToReview) return;
            setIsSubmitting(true);
            setReviewDocsError(null);
            setUserActionMessage(null);

            try {
                const response = await fetch('/api/admin_update_user_status.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user_id: userToReview.id, status: 'verified' })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || 'Failed to reactivate user.');
                setUserActionMessage(result.message);
                fetchAllUsers(); // Refresh user list
                setShowReviewModal(false);
            } catch (err) {
                setReviewDocsError(err.message);
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleRejectDocuments = async () => {
            if (!userToReview || !rejectionReason.trim()) {
                setRejectionError('A reason for rejection is required.');
                return;
            }
            setIsSubmitting(true);
            setRejectionError(null);
            setUserActionMessage(null);

            try {
                const response = await fetch('/api/admin_reject_documents.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: userToReview.id,
                        reason: rejectionReason
                    })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || 'Failed to reject documents.');

                setUserActionMessage(result.message);
                fetchAllUsers(); // Refresh user list
                setShowRejectReviewModal(false); // Close rejection modal
                setShowReviewModal(false); // Close review modal
            } catch (err) {
                setRejectionError(err.message);
            } finally {
                setIsSubmitting(false);
                setRejectionReason('');
            }
        };

        // --- NEW: Handler for updating user roles ---
        const handleConfirmRoleUpdate = async () => {
            if (!userToManageRoles || selectedRoles.length === 0) {
                setRoleError('At least one role must be selected.');
                return;
            }
            setIsSubmitting(true);
            setRoleError(null);
            setUserActionMessage(null);

            try {
                const response = await fetch('/api/admin_update_user_roles.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_id: userToManageRoles.id,
                        roles: selectedRoles
                    })
                });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || 'Failed to update user roles.');

                setUserActionMessage(result.message);
                fetchAllUsers(); // Refresh user list
                setShowRoleModal(false);
            } catch (err) {
                setRoleError(err.message);
            } finally {
                setIsSubmitting(false);
            }
        };

        // --- NEW: Handler for toggling role selection ---
        const handleRoleToggle = (role) => {
            setSelectedRoles(prev => {
                if (prev.includes(role)) {
                    return prev.filter(r => r !== role);
                } else {
                    return [...prev, role];
                }
            });
        };

        const confirmUserAction = async () => {
            const { user, action } = userActionModalConfig;
            if (!user || !action) return;
            setIsSubmitting(true);
            setUserActionMessage(null);
            try {
                let url, body;
                if (action === 'delete') {
                    url = '/api/admin_delete_user.php';
                    body = { user_id: user.id };
                } else {
                    url = '/api/admin_update_user_status.php';
                    let newStatus = '';
                    switch (action) {
                        case 'verify':
                        case 'un-suspend':
                            newStatus = 'verified';
                            break;
                        case 'suspend':
                            newStatus = 'suspended';
                            break;
                        case 'ban':
                            newStatus = 'banned';
                            break;
                        default:
                            throw new Error('Invalid user action specified.');
                    }
                    body = { user_id: user.id, status: newStatus };
                }
                const response = await fetch(url, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
                const result = await response.json();
                if (!response.ok) throw new Error(result.message || 'Failed to perform action.');
                setUserActionMessage(result.message);
                fetchAllUsers();
                fetchDashboardStats();
                setTimeout(() => { setShowUserActionModal(false); setUserActionMessage(null); }, 2000);
            } catch (err) {
                setUserActionMessage(`Error: ${err.message}`);
            } finally {
                setIsSubmitting(false);
            }
        };

        // --- Approval Logic for Pending Products ---
        const handleApproveClick = (product) => {
            setProductToApprove(product);
            setShowApproveOptionsModal(true);
            setFormError(null);
        };

        const handleConfirmApproval = async (destination) => {
            if (!productToApprove) return;
            setIsSubmitting(true);
            setActionMessage(null);
            setFormError(null);

            try {
                if (destination === 'marketplace') {
                    await updateListingStatus(productToApprove, 'approved');
                } else if (destination === 'wots') {
                    // Use the new, more flexible endpoint
                    const payload = {
                        product_id: productToApprove.id,
                        listing_type: productToApprove.listingType,
                        market: 'components' // The target market
                    };
                    const response = await fetch('/api/approve_to_market.php', { 
                        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) 
                    });
                    const result = await response.json();
                    if (!response.ok) throw new Error(result.message || 'Failed to approve to Components and Spares.');
                    setActionMessage({ type: 'success', text: result.message });
                    fetchWotsProducts();
                } else {
                    throw new Error('Invalid approval destination.');
                }
                fetchPendingProducts();
                fetchRecentProducts();
                fetchDashboardStats();
                setShowApproveOptionsModal(false);
                setProductToApprove(null);
            } catch (err) {
                setFormError(err.message);
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleRejectClick = (product) => {
            setProductToReject(product);
            setShowRejectModal(true);
        };

        const handleConfirmReject = async (product, reason) => {
            setActionMessage(null);
            try {
                const emailPayload = {
                    productName: product.name,
                    sellerName: product.seller_name,
                    sellerEmail: product.seller_email,
                    reason: reason,
                };
                const emailResponse = await fetch('/api/reject_product_email.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(emailPayload)
                });
                if (!emailResponse.ok) {
                    console.error("Failed to send rejection email, but proceeding with product rejection.");
                }
                await updateListingStatus(product, 'rejected');
                setShowRejectModal(false);
            } catch (err) {
                throw new Error(`Error during rejection process: ${err.message}`);
            }
        };

        const handleProductUpdate = () => {
            fetchPendingProducts();
            fetchRecentProducts();
        };

        // --- Pagination Logic ---
        const totalPendingPages = Math.ceil(pendingProducts.length / itemsPerPage);
        const currentPendingProducts = pendingProducts.slice((pendingPage - 1) * itemsPerPage, pendingPage * itemsPerPage);        
        const filteredRecentProducts = recentProducts.filter(item =>
            (item.name && item.name.toLowerCase().includes(recentSearchTerm.toLowerCase())) ||
            (item.listing_number && item.listing_number.toLowerCase().includes(recentSearchTerm.toLowerCase()))
        );
        const totalRecentPages = Math.ceil(filteredRecentProducts.length / itemsPerPage);
        const currentRecentProducts = filteredRecentProducts.slice((recentPage - 1) * itemsPerPage, recentPage * itemsPerPage);


        const totalWotsPages = Math.ceil(wotsProducts.length / itemsPerPage);
        const currentWotsProducts = wotsProducts.slice((wotsPage - 1) * itemsPerPage, wotsPage * itemsPerPage);
        const totalPendingOrdersPages = Math.ceil(pendingOrders.length / itemsPerPage);
        const currentPendingOrders = pendingOrders.slice((pendingOrdersPage - 1) * itemsPerPage, pendingOrdersPage * itemsPerPage);
        const totalPaidOrdersPages = Math.ceil(paidOrders.length / itemsPerPage);
        const currentPaidOrders = paidOrders.slice((paidOrdersPage - 1) * itemsPerPage, paidOrdersPage * itemsPerPage);
        const filteredUsers = allUsers.filter(u => {
            const matchesSearch = u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
                                  u.email.toLowerCase().includes(userSearchTerm.toLowerCase());
            const matchesRole = userRoleFilter === 'all' || (u.roles && u.roles.toLowerCase().includes(userRoleFilter));
            return matchesSearch && matchesRole;
        });
        const totalAllUserPages = Math.ceil(filteredUsers.length / itemsPerPage);
        const currentAllUsers = filteredUsers.slice((allUsersPage - 1) * itemsPerPage, allUsersPage * itemsPerPage);

        return (
            <div data-name="admin-dashboard" data-file="components/AdminDashboard.js" className="min-h-screen py-8">
                <div className="container mx-auto px-4">
                    <div className="mb-8">
                        <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter uppercase">Admin Dashboard</h1>
                        <p className="text-blue-400 font-bold uppercase tracking-[0.3em] text-xs mt-2">Marketplace Management & Oversight</p>
                    </div>

                    {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg text-sm mb-4">{error}</div>}
                    {actionMessage && (
                        <div className={`p-3 rounded-lg text-sm mb-4 ${actionMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {actionMessage.text}
                        </div>
                    )}

                    <div id="admin-stats" className="grid md:grid-cols-5 gap-6 mb-8">
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] mb-2">Total Revenue</h3>
                            {loadingStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <p className="text-3xl font-bold text-green-600">{formatPrice(totalRevenue)}</p>}
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] mb-2">Transactions</h3>
                            {loadingStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <p className="text-3xl font-bold text-blue-600">{totalTransactions}</p>}
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] mb-2">Pending Items</h3>
                            {loadingStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <p className="text-3xl font-bold text-amber-600">{pendingItemsCount}</p>}
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] mb-2">Active Users</h3>
                            {loadingStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <p className="text-3xl font-bold text-purple-600">{activeUsersCount}</p>}
                        </div>
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] mb-2">Diesel Quotes</h3>
                            {loadingStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <p className="text-3xl font-bold text-orange-600">{dieselQuotesCount || 0}</p>}
                        </div>
                    </div>

                    <div className="mb-8 flex flex-wrap gap-4">
                        <button
                            onClick={handleAddNewItemClick}
                            disabled={isCheckingProfile}
                            className="w-full md:w-auto primary-gradient text-white font-bold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center"
                        >
                            {isCheckingProfile ? <><i className="fas fa-spinner fa-spin mr-2"></i>Checking Profile...</> : <><i className="fas fa-plus-circle mr-2"></i>Add New Item</>}
                        </button>
                        <Link
                            to="/admin/platform-fees"
                            className="w-full md:w-auto bg-gray-700 text-white font-bold py-3 px-6 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center"
                        >
                            <i className="fas fa-percent mr-2"></i>Platform Fee Management
                        </Link>
                        <Link
                            to="/admin/notifications"
                            className="w-full md:w-auto bg-gray-700 text-white font-bold py-3 px-6 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center"
                        >
                            <i className="fas fa-bell mr-2"></i>Send Notifications
                        </Link>
                        <Link
                            to="/admin/diesel-quotes"
                            className="w-full md:w-auto bg-orange-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center"
                        >
                            <i className="fas fa-gas-pump mr-2"></i>Diesel Quotes
                        </Link>
                    </div>

                    {/* Seller Applications Section - Hidden During Development */}
                    {/* <div className="bg-white rounded-xl shadow-lg p-6">
                        <SellerApplicationsManager user={user} />
                    </div> */}

                    <div className="grid md:grid-cols-2 gap-8">
                        
                        {/* Pending Approvals */}
                        <div id="pending-approvals" className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6">Pending Approvals</h2>
                            <div className="space-y-4">
                                {loadingPendingProducts && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                                {!loadingPendingProducts && currentPendingProducts.length === 0 && <p className="text-gray-500 text-center">No pending items.</p>}
                                {!loadingPendingProducts && currentPendingProducts.map(item => (
                                    <div key={`${item.listingType}-${item.id}`} className="border rounded-lg p-4">
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h4 className="font-semibold text-gray-900">{item.name}</h4>
                                                <p className="text-gray-600 text-sm">by {item.seller_name} <span className="text-gray-400 font-mono text-xs">({item.listing_number})</span></p>
                                                <p className="text-gray-500 text-xs">{item.category_name} • {new Date(item.created_at).toLocaleDateString()}</p>
                                            </div>
                                            <p className="font-bold text-blue-600">{item.price ? formatPrice(item.price) : 'On Request'}</p>
                                        </div>
                                        <div className="flex space-x-2 mt-4">
                                            <button onClick={() => setProductToView(item)} className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors text-sm">View</button>
                                            <button onClick={() => handleApproveClick(item)} className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors text-sm">Approve</button>
                                            <button onClick={() => handleRejectClick(item)} className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors text-sm">Reject</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Pagination currentPage={pendingPage} totalPages={totalPendingPages} onPageChange={setPendingPage} />
                        </div>

                        {/* Recent Listings */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <div className="flex flex-col md:flex-row justify-between md:items-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0">Recently Approved</h2>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search by name or listing #"
                                        value={recentSearchTerm}
                                        onChange={(e) => setRecentSearchTerm(e.target.value)}
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-64"
                                    />
                                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                                </div>
                            </div>
                            <div className="space-y-4">
                                {loadingRecentProducts && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                                {!loadingRecentProducts && currentRecentProducts.length === 0 && <p className="text-gray-500 text-center">No recently approved listings.</p>}
                                {!loadingRecentProducts && currentRecentProducts.map(item => (
                                    <div key={`${item.listingType}-${item.id}`} className="border rounded-lg p-4">
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h4 className="font-semibold text-gray-900">{item.name}</h4>
                                                <p className="text-gray-600 text-sm">by {item.seller_name} <span className="text-gray-400 font-mono text-xs">({item.listing_number})</span></p>
                                                <p className="text-gray-500 text-xs">Approved: {new Date(item.updated_at).toLocaleDateString()}</p>
                                            </div>
                                            <p className="font-bold text-blue-600">{item.price ? formatPrice(item.price) : 'On Request'}</p>
                                        </div>
                                        <div className="flex space-x-2 mt-4">
                                            <button onClick={() => setProductToView(item)} className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors text-sm">View</button>
                                            <button onClick={() => handleEditClick(item)} className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors text-sm">Edit</button>
                                            <button onClick={() => handleDeleteClick(item)} className="flex-1 bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors text-sm">Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Pagination currentPage={recentPage} totalPages={totalRecentPages} onPageChange={setRecentPage} />
                        </div>
                    </div>

                    {/* Pending Transactions Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Pending Transactions (Awaiting Payment)</h2>
                        <div className="space-y-4">
                            {loadingPendingOrders && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                            {!loadingPendingOrders && currentPendingOrders.length === 0 && <p className="text-gray-500 text-center">No transactions are currently awaiting payment.</p>}
                            {!loadingPendingOrders && currentPendingOrders.map(order => (
                                <div key={order.id} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-semibold text-gray-900">{order.product_name} (x{order.quantity})</h4>
                                            <p className="text-gray-600 text-sm">Buyer: {order.buyer_name} <span className="text-gray-400 font-mono text-xs">({order.listing_number})</span></p>
                                            <p className="text-gray-500 text-xs">Order Date: {new Date(order.order_date).toLocaleDateString()}</p>
                                        </div>
                                        <p className="font-bold text-blue-600">{formatPrice(order.total_price)}</p>
                                    </div>
                                    <div className="flex justify-end space-x-2 mt-4">
                                        <button
                                            onClick={() => handleCancelClick(order)}
                                            className="bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors text-sm"
                                        >
                                            Cancel Transaction
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination currentPage={pendingOrdersPage} totalPages={totalPendingOrdersPages} onPageChange={setPendingOrdersPage} />
                    </div>

                    {/* Paid Transactions (Eligible for Reversal) Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Paid Transactions (Eligible for Reversal)</h2>
                        <div className="space-y-4">
                            {loadingPaidOrders && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                            {!loadingPaidOrders && currentPaidOrders.length === 0 && <p className="text-gray-500 text-center">No paid transactions are eligible for reversal.</p>}
                            {!loadingPaidOrders && currentPaidOrders.map(order => (
                                <div key={order.id} className="border rounded-lg p-4">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-semibold text-gray-900">{order.product_name} (x{order.quantity})</h4>
                                            <p className="text-gray-600 text-sm">Buyer: {order.buyer_name} <span className="text-gray-400 font-mono text-xs">({order.listing_number})</span></p>
                                            <p className="text-gray-500 text-xs">Seller: {order.seller_name}</p>
                                            <p className="text-gray-500 text-xs">Order Date: {new Date(order.order_date).toLocaleDateString()}</p>
                                            <p className="text-gray-500 text-xs">Type: {order.listing_type}</p>
                                        </div>
                                        <p className="font-bold text-blue-600">{formatPrice(order.total_price)}</p>
                                    </div>
                                    <div className="flex justify-end space-x-2 mt-4">
                                        <button
                                            onClick={() => handleCancelClick(order)}
                                            className="bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors text-sm"
                                        >
                                            Reverse Payment
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <Pagination currentPage={paidOrdersPage} totalPages={totalPaidOrdersPages} onPageChange={setPaidOrdersPage} />
                    </div>

                    {/* --- NEW: Price Requests Management Section --- */}
                    <PriceRequestsManager />

                    {/* --- NEW: Quote Requests Management Section --- */}
                    <QuoteRequestsManager onViewProductById={handleViewProductById} />

                    {/* --- NEW: Push Notification Manager --- */}
                    <NotificationManager />

                    {/* The "WOTS Listings Management" section has been hidden as requested. */}

                    {/* User Management Section */}
                    <div id="user-management" className="bg-white rounded-xl shadow-lg p-6 mt-8">
                        <div className="flex flex-col md:flex-row justify-between md:items-center mb-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4 md:mb-0">User Management</h2>
                            <div className="flex flex-col sm:flex-row gap-4">
                                {/* --- NEW: Role Filter Dropdown --- */}
                                <select
                                    value={userRoleFilter}
                                    onChange={(e) => setUserRoleFilter(e.target.value)}
                                    className="py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="all">All Roles</option>
                                    <option value="buyer">Buyers</option>
                                    <option value="seller">Sellers</option>
                                    <option value="admin">Admins</option>
                                </select>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Search by name or email..."
                                        value={userSearchTerm}
                                        onChange={(e) => setUserSearchTerm(e.target.value)}
                                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-64"
                                    />
                                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                                </div>
                            </div>
                        </div>
                        {userActionMessage && <div className={`p-3 rounded-lg text-sm mb-4 ${userActionMessage.startsWith('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{userActionMessage}</div>}
                        <div>
                            {loadingAllUsers && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                            {!loadingAllUsers && currentAllUsers.length === 0 && <p className="text-gray-500 text-center">No users found.</p>}
                            {!loadingAllUsers && currentAllUsers.length > 0 && (
                                <>
                                    <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg">
                                        <div className="md:col-span-2 text-[10px] font-black text-gray-900 uppercase tracking-widest">Name</div>
                                        <div className="md:col-span-3 text-[10px] font-black text-gray-900 uppercase tracking-widest">Email</div>
                                        <div className="md:col-span-1 text-[10px] font-black text-gray-900 uppercase tracking-widest">Roles</div>
                                        <div className="md:col-span-1 text-[10px] font-black text-gray-900 uppercase tracking-widest">Status</div>
                                        <div className="md:col-span-2 text-[10px] font-black text-gray-900 uppercase tracking-widest">Documentation</div>
                                        <div className="md:col-span-1 text-[10px] font-black text-gray-900 uppercase tracking-widest">Joined</div>
                                        <div className="md:col-span-2 text-[10px] font-black text-gray-900 uppercase tracking-widest text-right">Actions</div>
                                    </div>
                                    <div className="space-y-4 md:space-y-0">
                                        {currentAllUsers.map(u => (
                                            <div key={u.id} className="bg-white border rounded-lg p-4 grid grid-cols-2 gap-y-2 md:border-0 md:rounded-none md:grid-cols-12 md:gap-4 md:p-0 md:py-4 md:border-b items-center">
                                                <div className="col-span-2 md:col-span-2">
                                                    <div className="text-xs text-gray-500 md:hidden">Name</div>
                                                    <div className="text-sm font-medium text-gray-900">{u.name}</div>
                                                </div>
                                                <div className="col-span-2 md:col-span-3">
                                                    <div className="text-xs text-gray-500 md:hidden">Email</div>
                                                    <div className="text-sm text-gray-500 break-all">{u.email}</div>
                                                </div>
                                                <div className="col-span-1">
                                                    <div className="text-xs text-gray-500 md:hidden">Roles</div>
                                                    <div className="text-sm text-gray-500 capitalize">{u.roles}</div>
                                                </div>
                                                <div className="col-span-1 md:col-span-1">
                                                    <div className="text-xs text-gray-500 md:hidden">Status</div>
                                                    <div>
                                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${
                                                            u.status === 'verified' ? 'bg-green-100 text-green-800' :
                                                            u.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                            u.status === 'pending_review' ? 'bg-blue-100 text-blue-800' :
                                                            u.status === 'suspended' ? 'bg-orange-100 text-orange-800' :
                                                            u.status === 'banned' ? 'bg-red-100 text-red-800' :
                                                            'bg-gray-100 text-gray-800'
                                                        }`}>
                                                            {u.status.replace('_', ' ')}
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* --- NEW: Documentation Column --- */}
                                                <div className="col-span-2 md:col-span-2">
                                                    <div className="text-xs text-gray-900 md:hidden">Documentation</div>
                                                    <button onClick={() => handleReviewDocuments(u)} className="text-blue-600 hover:text-blue-800 underline text-sm font-medium">
                                                        View Documents
                                                    </button>
                                                </div>
                                                <div className="col-span-1">
                                                    <div className="text-xs text-gray-900 md:hidden">Joined</div>
                                                    <div className="text-sm text-gray-900">{new Date(u.created_at).toLocaleDateString()}</div>
                                                </div>
                                                <div className="col-span-2 md:col-span-2 flex justify-end items-center gap-4">
                                                    <UserActionsDropdown user={u} onAction={handleUserAction}/>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            <Pagination currentPage={allUsersPage} totalPages={totalAllUserPages} onPageChange={setAllUsersPage} />
                        </div>
                    </div>
                </div>

                {/* --- MODALS --- */}
                {/* --- NEW: Profile Incomplete Modal --- */}
                <ProfileIncompleteModal
                    isOpen={showProfileIncompleteModal}
                    onClose={() => setShowProfileIncompleteModal(false)}
                    missingFields={missingProfileFields}
                />

                {productToView && (
                    <ProductModal
                        product={productToView}
                        isOpen={!!productToView}
                        onClose={() => setProductToView(null)}
                        user={user}
                        onProductUpdate={handleProductUpdate}
                    />
                )}
                {showRejectModal && (
                    <RejectProductModal
                        isOpen={showRejectModal}
                        onClose={() => setShowRejectModal(false)}
                        product={productToReject}
                        onConfirmReject={handleConfirmReject}
                    />
                )}
                {showAddItemWizard && (
                    <AddItemWizard 
                        isOpen={showAddItemWizard}
                        onClose={() => setShowAddItemWizard(false)}
                        categories={categories}
                        user={user}
                        onListingAdded={handleListingAdded}
                    />
                )}
                {showUserActionModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-8 max-w-md w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">{userActionModalConfig.title}</h2>
                            <p className="text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: userActionModalConfig.message }}></p>
                            {userActionMessage && <div className={`text-sm mb-4 p-2 rounded ${userActionMessage.startsWith('Error') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{userActionMessage}</div>}
                            <div className="flex justify-end space-x-4">
                                <button onClick={() => setShowUserActionModal(false)} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50" disabled={isSubmitting}>Cancel</button>
                                <button 
                                    onClick={confirmUserAction} 
                                    disabled={isSubmitting} 
                                    className={`text-white py-2 px-4 rounded-lg transition-opacity disabled:opacity-50 ${userActionModalConfig.buttonClass}`}>
                                    {isSubmitting ? 'Processing...' : userActionModalConfig.buttonText}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {showApproveOptionsModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-8 max-w-md w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Approve Product: {productToApprove?.name}</h2>
                            <p className="text-gray-600 mb-6">Choose where to approve this product:</p>
                            {formError && <div className="text-red-500 text-sm mb-4 p-2 bg-red-100 rounded">{formError}</div>}
                            <div className="flex flex-col space-y-4">
                                <button
                                    onClick={() => handleConfirmApproval('marketplace')}
                                    disabled={isSubmitting}
                                    className="primary-gradient text-white py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Processing...' : 'Approve to Standard Marketplace'}
                                </button>
                                <button
                                    onClick={() => handleConfirmApproval('wots')}
                                    disabled={isSubmitting}
                                    className="bg-blue-700 text-white py-3 rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Processing...' : 'Approve to Components and Spares'}
                                </button>
                                <button onClick={() => { setShowApproveOptionsModal(false); setProductToApprove(null); setFormError(null); }} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50" disabled={isSubmitting}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}
                {showCancelConfirmModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-8 max-w-md w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Cancellation</h2>
                            <p className="text-gray-600 mb-6">Are you sure you want to cancel the transaction for "<strong>{orderToCancel?.product_name}</strong>"? This will relist the product and cannot be undone.</p>
                            {cancelError && <div className="text-red-500 text-sm mb-4 p-2 bg-red-100 rounded">{cancelError}</div>}
                            <div className="flex justify-end space-x-4">
                                <button onClick={() => setShowCancelConfirmModal(false)} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50" disabled={isCancelling}>Back</button>
                                <button onClick={confirmCancelTransaction} disabled={isCancelling} className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50">
                                    {isCancelling ? 'Cancelling...' : 'Confirm Cancellation'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- NEW: Modal for entering suspension reason --- */}
                {showSuspendModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-8 max-w-lg w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Suspend User: {userToSuspend?.name}</h2>
                            <p className="text-gray-600 mb-4">Please provide a clear reason for the suspension. This message will be shown to the seller upon their next login.</p>
                            
                            <textarea
                                value={suspensionReason}
                                onChange={(e) => setSuspensionReason(e.target.value)}
                                placeholder="e.g., Your CIPC documents are out of date. Please upload a valid copy to reactivate your account."
                                rows="4"
                                className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                            ></textarea>
                            {suspensionError && <div className="text-red-500 text-sm mt-2">{suspensionError}</div>}
                            <div className="flex justify-end mt-6 space-x-4">
                                <button onClick={() => setShowSuspendModal(false)} disabled={isSubmitting} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                                <button onClick={handleConfirmSuspension} disabled={isSubmitting} className="bg-yellow-500 text-white py-2 px-4 rounded-lg hover:bg-yellow-600 disabled:opacity-50">{isSubmitting ? 'Suspending...' : 'Confirm Suspension'}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- NEW: Modal for reviewing suspension documents --- */}
                {showReviewModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-8 max-w-lg w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Review Documents for: {userToReview?.name}</h2>
                            {loadingReviewDocs && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                            {reviewDocsError && <div className="text-red-500 text-sm mt-2 p-2 bg-red-50 rounded">{reviewDocsError}</div>}
                            {!loadingReviewDocs && !reviewDocsError && (
                                <>
                                    {reviewDocuments.length > 0 ? (
                                        <div className="space-y-3 mb-6">
                                            {reviewDocuments.map(doc => (
                                                <a
                                                    key={doc.id}
                                                    href={`/miners-market-main/${doc.file_path}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block bg-gray-100 p-3 rounded-lg hover:bg-gray-200 transition-colors"
                                                >
                                                    <i className="fas fa-file-alt text-gray-500 mr-3"></i>
                                                    <span className="font-medium text-gray-800">{doc.document_name}</span>
                                                    <span className="text-xs text-gray-500 float-right mt-1">{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                                                </a>
                                            ))}
                                        </div>
                                    ) : <p className="text-gray-500 text-center my-6">No documents were found for this review request.</p>}
                                    <div className="flex justify-end mt-6 space-x-4">
                                        <button onClick={() => setShowReviewModal(false)} disabled={isSubmitting} className="bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400 disabled:opacity-50">Close</button>
                                        <button onClick={() => setShowRejectReviewModal(true)} disabled={isSubmitting || reviewDocuments.length === 0} className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50">Reject</button>
                                        <button onClick={handleApproveDocuments} disabled={isSubmitting} className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50">{isSubmitting ? 'Reactivating...' : 'Approve & Reactivate'}</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* --- NEW: Modal for entering rejection reason --- */}
                {showRejectReviewModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[51] p-4">
                        <div className="bg-white rounded-xl p-8 max-w-lg w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Reject Documents for: {userToReview?.name}</h2>
                            <p className="text-gray-600 mb-4">Provide a reason for the rejection. This message will be emailed to the user, and they will be prompted to re-upload documents.</p>
                            <textarea
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                placeholder="e.g., The submitted ID document is blurry. Please upload a clearer copy."
                                rows="4"
                                className="w-full p-2 border border-gray-300 rounded-md shadow-sm"
                            ></textarea>
                            {rejectionError && <div className="text-red-500 text-sm mt-2">{rejectionError}</div>}
                            <div className="flex justify-end mt-6 space-x-4">
                                <button onClick={() => setShowRejectReviewModal(false)} disabled={isSubmitting} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                                <button onClick={handleRejectDocuments} disabled={isSubmitting} className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50">{isSubmitting ? 'Submitting...' : 'Confirm Rejection'}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- NEW: Modal for managing user roles --- */}
                {showRoleModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-8 max-w-lg w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Manage Roles for: {userToManageRoles?.name}</h2>
                            <p className="text-gray-600 mb-4">Select the roles you want to assign to this user. Multiple roles can be selected.</p>
                            
                            <div className="space-y-3 mb-6">
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedRoles.includes('buyer')}
                                        onChange={() => handleRoleToggle('buyer')}
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700 font-medium">Buyer</span>
                                </label>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedRoles.includes('seller')}
                                        onChange={() => handleRoleToggle('seller')}
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700 font-medium">Seller</span>
                                </label>
                                <label className="flex items-center space-x-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={selectedRoles.includes('admin')}
                                        onChange={() => handleRoleToggle('admin')}
                                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700 font-medium">Admin</span>
                                </label>
                            </div>
                            
                            {roleError && <div className="text-red-500 text-sm mb-4 p-2 bg-red-50 rounded">{roleError}</div>}
                            
                            <div className="flex justify-end mt-6 space-x-4">
                                <button onClick={() => setShowRoleModal(false)} disabled={isSubmitting} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                                <button onClick={handleConfirmRoleUpdate} disabled={isSubmitting} className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50">{isSubmitting ? 'Updating...' : 'Update Roles'}</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- NEW: Universal Edit & Delete Modals --- */}
                {showEditListingModal && (
                    <EditListingModal
                        isOpen={showEditListingModal}
                        onClose={() => setShowEditListingModal(false)}
                        listing={listingToEdit}
                        onListingUpdated={handleListingUpdated}
                        categories={categories}
                    />
                )}
                {showDeleteConfirmModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-8 max-w-md w-full">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Deletion</h2>
                            <p className="text-gray-600 mb-6">Are you sure you want to permanently delete "<strong>{listingToDelete?.name}</strong>"? This action cannot be undone.</p>
                            {actionMessage && actionMessage.type === 'error' && <div className="text-red-500 text-sm mb-4">{actionMessage.text}</div>}
                            <div className="flex justify-end space-x-4">
                                <button onClick={() => setShowDeleteConfirmModal(false)} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300">Cancel</button>
                                <button onClick={confirmDelete} disabled={isSubmitting} className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50">
                                    {isSubmitting ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
}

export default AdminDashboard;
